import Vue from "vue";
import * as Vuex from "vuex";
import { getStoreAccessors } from "vuex-typescript";
import { State } from "./state";
import * as types from "./mutation-types";
import * as Logger from "js-logger";
import { HTTP } from "../plugins/axios";
import Devotee from "../models/Devotee";
import Product from "../models/Product";

Vue.use(Vuex);

type Context = Vuex.ActionContext<State, State>;

const state: State = {
  splashScreenActive: false,
  loaderActive: false,

  auth: {
    isLoggedIn: false
  },
  order: undefined,
  devotee: new Devotee(),
  totalDonation: 0.0,
  fees: 0.0,
  added: [],
  all: [],
  dharmam: "sri-krishna-premamrutham" /* Krishna Premamrutham */,
  notes: "",
  devoteeType: ""
};

const getters = {
  getSplashScreenState(state: State): boolean {
    return state.splashScreenActive;
  },
  getLoaderState(state: State): boolean {
    return state.loaderActive;
  },
  getLoggedInState(state: State): boolean {
    return state.auth.isLoggedIn;
  },
  getDevotee(state: State): Devotee {
    return state.devotee;
  },
  getTotalDonation(state: State): number {
    return state.totalDonation;
  },
  getFees(state: State): number {
    return state.fees;
  },
  getNotes(state: State): string {
    return state.notes;
  },
  getDevoteeType(state: State): string {
    return state.devoteeType;
  },
  isInBasket(state: State): any {
    return (product: any) => {
      let found = state.added.findIndex(id => id === product.id) >= 0;
      // Logger.debug("isInBasket Id ", product.id, found);
      return found;
    };
  },
  allProducts: (state: State): Array<Product> => state.all, // would need action/mutation if data fetched async
  getOrder: (state: State): any => state.order,
  getNumberOfProducts: (state: State): number =>
    state.all ? state.all.length : 0,
  cartProducts: (state: State): Array<Product> => {
    return state.added.map(id => {
      let product = state.all.find(p => p.id === id);
      product = product === undefined ? new Product() : product;
      return {
        id: product.id,
        title: product.title,
        description: product.description,
        donation: product.donation,
        name: product.name,
        duration: product.duration,
        size: product.size,
        type: product.type,
        dharmamId: product.dharmamId
      };
    });
  }
};

const mutations = {
  setSplashScreenVisibility(state: State, splashScreenState: boolean) {
    state.splashScreenActive = splashScreenState;
  },
  setDevotee(state: State, devotee: Devotee) {
    state.devotee = devotee;
  },
  setTotalDonation(state: State, totalDonation: number) {
    state.totalDonation = totalDonation;
  },
  setFees(state: State, fees: number) {
    state.fees = fees;
  },
  setNotes(state: State, notes: string) {
    state.notes = notes;
  },
  setDevoteeType(state: State, devoteeType: string) {
    state.devoteeType = devoteeType;
  },
  setLoaderVisibility(state: State, loaderState: boolean) {
    state.loaderActive = loaderState;
  },
  setLoggedInState(state: State, loggedInState: boolean) {
    state.auth.isLoggedIn = loggedInState;
  },
  setProducts(state: State, products: any) {
    state.all = products;
  },
  setOrder(state: State, order: any) {
    state.order = order;
  },
  addToBasket(state: State, product: Product) {
    const record = state.added.find(id => id === product.id);
    if (!record) {
      state.added.push(product.id);
    } else {
      // record.quantity++;
    }
  },
  removeFromBasket(state: State, product: Product) {
    const index = state.added.findIndex(id => id === product.id);

    Logger.debug("Product Id ", product, index);
    if (index >= 0) {
      state.added.splice(index, 1);
    } else {
      // record.quantity++;
    }
  }
};

const actions = {
  loginUser(context: Context, loggedInState: boolean) {
    commitLoggedInState(context, loggedInState);
  },
  addToCart(context: Context, product: Product) {
    commitBasket(context, product);
  },
  removeFromCart(context: Context, product: Product) {
    commitFromBasket(context, product);
  },
  loadProducts(context: Context) {
    return new Promise((resolve, reject) => {
      if (context.getters.allProducts.length == 0) {
        commitLoaderVisibility(context, true);
        HTTP.get("products")
          .then((response: any) => {
            // Logger.debug("Response ", response.data);
            commitProducts(context, response.data);
            resolve(response.data);
            commitLoaderVisibility(context, false);
          })
          .catch((e: any) => {
            reject(e);
            commitLoaderVisibility(context, false);
          });
      } else {
        resolve(context.getters.allProducts);
      }
    });
  },
  getOrderDetails(context: Context, uuid: string) {
    return new Promise((resolve, reject) => {
      HTTP.get("order/" + uuid)
        .then((response: any) => {
          // Logger.debug("Response ", response.data);
          commitOrder(context, response.data);
          resolve(response.data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  },
  placeOrder(context: Context) {
    return new Promise((resolve, reject) => {
      Logger.debug("Placing Order");
      let data = context.state.devotee;
      (data["items"] = context.state.added),
        (data["amount"] = context.state.totalDonation),
        (data["fees"] = context.state.fees),
        (data["notes"] = context.state.notes),
        (data["type"] = context.state.devoteeType);

      HTTP.post("/v2/checkout", data)
        .then((response: any) => {
          Logger.debug("Response ", response.data);
          resolve(response.data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  },
  calculateFees(context: Context) {
    return new Promise((resolve, reject) => {
      Logger.debug("Calculating Fees");
      let data: any = context.state.devotee;
      data["items"] = context.state.added;
      data["amount"] = context.state.totalDonation;
      data["type"] = context.state.devoteeType;

      HTTP.post("/v2/calculate-fees", data)
        .then((response: any) => {
          Logger.debug("Response ", response.data);
          resolve(response.data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  },
  confirmPayment(context: Context, data: any) {
    return new Promise((resolve, reject) => {
      Logger.debug("Placing Order");
      data["type"] = context.state.devoteeType;
      HTTP.post("/v2/verify", data)
        .then((response: any) => {
          Logger.debug("Response ", response.data);
          resolve(response.data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  }
};

export const createStore = () =>
  new Vuex.Store<State>({
    state: state,
    getters: getters,
    mutations: mutations,
    actions: actions
  });

const { read, commit, dispatch } = getStoreAccessors<State, State>("");

/*************************************************/
/* GETTERS */
/*************************************************/
export const readSplashScreenVisibility = read(getters.getSplashScreenState);
export const readLoaderVisibility = read(getters.getLoaderState);
export const readLoggedInState = read(getters.getLoggedInState);
export const readProducts = read(getters.allProducts);
export const readDevotee = read(getters.getDevotee);
export const readBasket = read(getters.cartProducts);
export const readTotalDonation = read(getters.getTotalDonation);
export const readFees = read(getters.getFees);
export const readNotes = read(getters.getNotes);
export const readDevoteeType = read(getters.getDevoteeType);
export const isInBasket = read(getters.isInBasket);
export const readOrder = read(getters.getOrder);

/*************************************************/
/* MUTATIONS */
/*************************************************/
export const commitSplashScreenVisibility = commit(
  mutations.setSplashScreenVisibility
);
export const commitLoaderVisibility = commit(mutations.setLoaderVisibility);
export const commitLoggedInState = commit(mutations.setLoggedInState);
export const commitDevotee = commit(mutations.setDevotee);
export const commitBasket = commit(mutations.addToBasket);
export const commitFromBasket = commit(mutations.removeFromBasket);
export const commitProducts = commit(mutations.setProducts);
export const commitOrder = commit(mutations.setOrder);
export const commitTotalDonation = commit(mutations.setTotalDonation);
export const commitFees = commit(mutations.setFees);
export const commitNotes = commit(mutations.setNotes);
export const commitDevoteeType = commit(mutations.setDevoteeType);

/*************************************************/
/* ACTIONS */
/*************************************************/
export const dispatchLoginUser = dispatch(actions.loginUser);
export const loadProducts = dispatch(actions.loadProducts);
export const placeOrder = dispatch(actions.placeOrder);
export const calculateFees = dispatch(actions.calculateFees);
export const getOrderDetails = dispatch(actions.getOrderDetails);
export const confirmPayment = dispatch(actions.confirmPayment);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./src/store/store.ts